import { Circle, Heart, Hexagon, Minus, Spline, Square, Star, Triangle } from 'lucide-react'
import { Button } from '@components/ui/button'
import { Pentagon, Starone, Startwo } from '@components/ui/custom-icons'
import { cn } from '@/utils'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import React from 'react'
import { PenToolButtonIcon } from './components/pen-tool-button-icon'
import { PointTypeButton } from './components/point-type-button'

interface ShapesToolProps {
  selectedShape: ShapeType | null
  setSelectedShape: (shape: ShapeType | null) => void
  selectedPenTool: string | null
  setSelectedPenTool: (tool: string | null) => void
  selectedPointType: string | null
  setSelectedPointType: (type: string | null) => void
  onSwitchToSelect?: () => void
  /** When true the user is in node-edit mode — show pen± icon while pen tool is active. */
  isNodeEditMode?: boolean
}

const SHAPE_ITEMS: Array<{ key: ShapeType; Icon: React.ElementType; label: string }> = [
  { key: 'square', Icon: Square, label: 'Rectangle' },
  { key: 'circle', Icon: Circle, label: 'Circle' },
  { key: 'triangle', Icon: Triangle, label: 'Triangle' },
  { key: 'star', Icon: Star, label: '5 Point Star' },
  { key: 'starone', Icon: Starone, label: 'Star Burst 1' },
  { key: 'startwo', Icon: Startwo, label: 'Star Burst 2' },
  { key: 'hexagon', Icon: Hexagon, label: 'Hexagon' },
  { key: 'pentagon', Icon: Pentagon, label: 'Pentagon' },
  { key: 'heart', Icon: Heart, label: 'Heart' },
]

export const ShapesTool: React.FC<ShapesToolProps> = ({
  selectedShape,
  setSelectedShape,
  selectedPenTool,
  setSelectedPenTool,
  selectedPointType,
  setSelectedPointType,
  isNodeEditMode = false,
}) => {
  const isPenEditMode = isNodeEditMode && selectedPenTool === 'pen'

  return (
    <div className="space-y-4">
      {/* Add Shapes Section */}
      <div>
        <label className="text-xs text-muted-foreground">Add Shapes</label>
      </div>

      {/* Shapes Section */}
      <div>
        <label className="text-sm font-semibold text-foreground">Shapes</label>
        <div className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-1">
          {SHAPE_ITEMS.map(({ key, Icon, label }) => {
            const isActive = selectedShape === key

            return (
              <Button
                key={key}
                variant="ghost"
                size="icon"
                aria-pressed={isActive}
                aria-label={label}
                onClick={() => {
                  if (!isActive) {
                    setSelectedShape(key as ShapeType)
                    setSelectedPenTool(null)
                  }
                }}
                className={cn(
                  !isActive && 'text-muted-foreground',
                  isActive && 'bg-accent text-accent-foreground'
                )}
              >
                <Icon className="w-4 h-4" />
              </Button>
            )
          })}
        </div>
      </div>

      {/* Separator */}
      <div className="border-t border-border my-4"></div>

      {/* Draw Shapes Section */}
      <div>
        <label className="text-xs text-muted-foreground">Draw Shapes</label>
      </div>

      {/* Pen Tool Section */}
      <div>
        <label className="text-sm font-semibold text-foreground">Pen Tool</label>
        <div className="mt-2 flex gap-1">
          <Button
            variant="ghost"
            size="icon"
            aria-pressed={selectedPenTool === 'pen' || isNodeEditMode}
            aria-label={isPenEditMode ? 'Pen edit mode (add/remove nodes)' : 'Pen tool'}
            onClick={() => {
              const next = selectedPenTool === 'pen' ? null : 'pen'

              setSelectedPenTool(next)
              if (next === 'pen') {
                setSelectedShape(null)
                if (!selectedPointType) {
                  setSelectedPointType('curve')
                }
              }
            }}
            className={cn(
              selectedPenTool !== 'pen' && !isNodeEditMode && 'text-muted-foreground',
              (selectedPenTool === 'pen' || isNodeEditMode) && 'bg-accent text-accent-foreground'
            )}
          >
            <PenToolButtonIcon isPenEditMode={isPenEditMode} isNodeEditMode={isNodeEditMode} />
          </Button>
        </div>
      </div>

      {/* Point Type Section */}
      <div>
        <label className="text-sm font-semibold text-foreground">Point Type</label>
        <div className="mt-2 flex gap-1">
          <PointTypeButton
            pointType="curve"
            label="Curve point"
            Icon={Spline}
            selectedPointType={selectedPointType}
            setSelectedPointType={setSelectedPointType}
          />
          <PointTypeButton
            pointType="straight"
            label="Straight point"
            Icon={Minus}
            selectedPointType={selectedPointType}
            setSelectedPointType={setSelectedPointType}
          />
        </div>
      </div>
    </div>
  )
}
